// ============================================================================
// CHANGO V5.0 — Cross-SPA URL helpers (Task #55)
// ============================================================================
// After the 4-SPA split (Task #53) the frontend runs under three subdomains
// (site / app / ops) in prod but all under a single Vite origin in dev. A
// hard-coded `<Link to="/login">` therefore means:
//
//   dev   (single Vite server)          → /login              ← works
//   prod  (marketing page on site host) → chango-tech.com/login
//                                          which doesn't exist ← 404
//
// This module is the single place that turns a "logical" intra-platform
// path into the correct absolute or relative URL for the current deploy.
//
// Env contract:
//   VITE_SITE_HOST  e.g. "https://chango-tech.com"      (marketing + capability)
//   VITE_APP_HOST   e.g. "https://app.chango-tech.com"  (auth + tenant)
//   VITE_OPS_HOST   e.g. "https://ops.chango-tech.com"  (admin console)
//
// Any env var that is missing, empty, or equal to the *current* window origin
// causes the helper to return a relative path — so dev (single Vite origin,
// env unset) keeps working identically to before the split.
//
// Helpers:
//   siteUrl(path)   → URL on the marketing host
//   appUrl(path)    → URL on the auth+tenant host
//   opsUrl(path)    → URL on the admin host
//   resolveHost(kind) → the configured absolute origin (or '' if relative)
//
// All helpers are pure and SSR-safe: if `window` is undefined they still
// return the input path as-is. Import them with `import { appUrl } from …`
// and use in `<Link to>` / `<a href>` identically to a string.
// ============================================================================

function readEnv(key) {
  // Vite inlines import.meta.env at build time. Guard for non-Vite test
  // environments (node --test) where import.meta.env may be undefined.
  try {
    const v = import.meta?.env?.[key];
    if (typeof v === 'string') return v;
  } catch {
    /* non-Vite runtime */
  }
  // Fallback: node process.env (useful for unit tests).
  if (typeof process !== 'undefined' && process?.env?.[key]) {
    return process.env[key];
  }
  return '';
}

function currentOrigin() {
  if (typeof window === 'undefined') return '';
  try {
    return window.location.origin;
  } catch {
    return '';
  }
}

// Strip trailing slashes from the input host string. Implemented with a
// hand-rolled loop rather than a regex so the offline heuristic balance
// checker (which does not model regex literals) can't mis-tokenize the
// escaped slashes in `/\/+$/`.
function trimTrailingSlash(s) {
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 47 /* '/' */) end -= 1;
  return s.slice(0, end);
}

function hasHttpScheme(s) {
  // Equivalent to /^https?:\/\//i without the backslash-slash regex syntax
  // that confuses the offline balance checker.
  const lc = s.slice(0, 8).toLowerCase();
  return lc.startsWith('http://') || lc.startsWith('https://');
}

function normalizeHost(v) {
  if (!v || typeof v !== 'string') return '';
  const s = trimTrailingSlash(v.trim());
  if (!s) return '';
  // Allow bare host like "www.chango-tech.com" by prefixing a default scheme.
  if (!hasHttpScheme(s)) return 'https://' + s;
  return s;
}

// Map SPA kind → Vite env var name. Pre-declared as a module-scope constant
// (not an immediate-indexed object literal) so static parsers/balance-checkers
// can track brace pairing without false positives.
const KIND_ENV_KEY = Object.freeze({
  site: 'VITE_SITE_HOST',
  app:  'VITE_APP_HOST',
  ops:  'VITE_OPS_HOST'
});

const OFFICIAL_HOST = Object.freeze({
  site: 'https://chango-tech.com',
  app:  'https://app.chango-tech.com',
  ops:  'https://ops.chango-tech.com'
});

function officialKindFromOrigin(origin) {
  const host = normalizeHost(origin);
  if (!host) return '';
  if (host === OFFICIAL_HOST.site || host === 'https://www.chango-tech.com') return 'site';
  if (host === OFFICIAL_HOST.app) return 'app';
  if (host === OFFICIAL_HOST.ops) return 'ops';
  return '';
}

export function resolveHost(kind) {
  const key = KIND_ENV_KEY[kind];
  if (!key) return '';
  const here = currentOrigin();
  const currentKind = officialKindFromOrigin(here);
  let configured = normalizeHost(readEnv(key));

  // Production safety net: the official marketing host must always send
  // product/login/trial CTAs to app.chango-tech.com even if an env value is
  // missing or mistakenly points back to the site host. Local dev keeps the
  // relative-path behaviour because currentKind is empty on localhost.
  if (currentKind && !configured) configured = OFFICIAL_HOST[kind] || '';
  if (kind === 'app' && currentKind === 'site') return OFFICIAL_HOST.app;
  if (kind === 'site' && currentKind === 'app') return OFFICIAL_HOST.site;

  if (!configured) return '';
  // Same-origin → keep relative so dev (single Vite origin) still works.
  if (here && configured === here) return '';
  return configured;
}

function build(kind, path) {
  const p = (typeof path === 'string' && path.length > 0) ? path : '/';
  const clean = p.startsWith('/') ? p : `/${p}`;
  const host = resolveHost(kind);
  return host ? `${host}${clean}` : clean;
}

export function siteUrl(path) { return build('site', path); }
export function appUrl(path)  { return build('app',  path); }
export function opsUrl(path)  { return build('ops',  path); }

// ----------------------------------------------------------------------------
// Cross-SPA dispatch (Task #58)
//
// Many places — CMS-authored CTAs, shared components, deep-linked emails —
// receive a bare intra-platform path without knowing which SPA owns it.
// `hostFor` classifies the path and `urlFor` returns the correct absolute
// (or relative, in dev) URL. Kept in sync with the e2e helper's dispatch.
//
// Classification rules (path prefix → SPA):
//   /ops/*                                   → ops
//   /s/* , /m/*                              → public  (capability URLs live
//                                                       on site host today)
//   /, /pricing, /product, /matrix, /cases,
//   /help, /blog, /about, /contact, /legal/* → site    (marketing)
//   everything else (incl. /login, /register,
//   /forgot-password, /reset-password, /verify,
//   /2fa, /app/*, /workspace/*, /dashboard)  → app
//
// Paths that don't start with `/` (e.g. `https://…`, `mailto:`, `tel:`, `#x`)
// are returned unchanged — those aren't intra-platform.
// ----------------------------------------------------------------------------

export function hostFor(rawPath) {
  if (typeof rawPath !== 'string' || !rawPath.startsWith('/')) return 'external';
  const p = rawPath;
  if (p.startsWith('/ops'))                            return 'ops';
  if (p.startsWith('/s/') || p.startsWith('/m/'))      return 'public';
  if (p === '/' || p.startsWith('/pricing')
      || p.startsWith('/product') || p.startsWith('/cases')
      || p.startsWith('/help')    || p.startsWith('/blog')
      || p.startsWith('/about')   || p.startsWith('/contact')
      || p.startsWith('/matrix')  || p.startsWith('/legal'))
    return 'site';
  return 'app';
}

export function urlFor(rawPath) {
  const kind = hostFor(rawPath);
  switch (kind) {
    case 'site':     return siteUrl(rawPath);
    case 'app':      return appUrl(rawPath);
    case 'ops':      return opsUrl(rawPath);
    case 'public':   return siteUrl(rawPath); // public capability URLs share site host
    case 'external': // fallthrough — not intra-platform, return as-is
    default:         return rawPath;
  }
}

export default { siteUrl, appUrl, opsUrl, resolveHost, hostFor, urlFor };
